import type { RcMessage } from './rcClient';

// ⚡ 증분 동기화(delta sync) 병합 — 기존 보유 메시지에 delta(놓친 메시지)를 합친다.
//   - _id 기준 중복 제거 (oldest=inclusive:false 라도 경계/재전송 중복 방어)
//   - ts 오름차순 정렬 (RC delta 는 최신순 desc 로 옴 → 화면 순서로 정렬)
//   - 입력을 변형하지 않는 순수 함수 (회귀 테스트 대상)
export function mergeDeltaMessages(prev: RcMessage[], delta: RcMessage[]): RcMessage[] {
	if (delta.length === 0) return prev;
	const seen = new Set(prev.map((m) => m._id));
	const fresh = delta.filter((m) => !seen.has(m._id));
	if (fresh.length === 0) return prev;
	const merged = [...prev, ...fresh];
	merged.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
	return merged;
}

// ⬆ 위로 스크롤 이전 페이지 붙이기 (2026-10-27 형 신고 — 정지윤님 데탑 ☆EMS 방 같은 메시지 카드가 3번 이상 반복).
//   종전 loadMoreMessages 는 '호출 시점 messages' 로 중복을 걸렀다가 setMessages(prev => [...fresh, ...prev]) 로 붙여,
//   스크롤 이벤트가 연달아 와 같은 페이지 요청이 겹치면 같은 묶음이 겹친 횟수만큼 앞에 붙었다.
//   → **현재 상태(prev)** 기준으로 거른다. 새로 붙일 게 없으면 입력 배열을 그대로(참조 동일 → 렌더 없음).
export function prependOlderMessages(prev: RcMessage[], older: RcMessage[]): RcMessage[] {
	if (older.length === 0) return prev;
	const seen = new Set(prev.map((m) => m._id));
	const fresh: RcMessage[] = [];
	for (const m of older) { if (!seen.has(m._id)) { seen.add(m._id); fresh.push(m); } }
	return fresh.length ? [...fresh, ...prev] : prev;
}

// 🛡 화면 직전 최종 중복 제거 — 어느 경로로 같은 _id 가 두 번 들어와도 한 번만 그린다(나중 항목 = 더 최신 사본으로).
//   순서는 첫 등장 위치 유지(정렬은 호출측 foldThreadReplies 가 다시 한다). 중복이 없으면 입력 그대로.
export function uniqueMessagesById<T extends { _id: string }>(list: readonly T[]): readonly T[] {
	const idx = new Map<string, number>();
	let dup = false;
	const out: T[] = [];
	for (const m of list) {
		const i = idx.get(m._id);
		if (i === undefined) { idx.set(m._id, out.length); out.push(m); } else { out[i] = m; dup = true; }
	}
	return dup ? out : list;
}

// ✏ 수정·삭제 동기화 기준 (2026-10-15 사용자 신고 "폰에서 쓴 글을 노트북에서 수정 → 노트북에만 수정 전이 보임")
//   보유 메시지 중 가장 최근에 바뀐 시각(_updatedAt, 없으면 ts). ts 기준 delta 는 '새 메시지'만 잡아
//   수정된 기존 메시지를 놓친다 — 이 시각 이후 바뀐 것을 chat.syncMessages 로 받는다. _pending 제외.
const tsMs = (s?: string) => { const n = s ? Date.parse(s) : NaN; return Number.isFinite(n) ? n : 0; };
export function newestUpdatedAt(messages: RcMessage[]): string | null {
	let best: string | null = null;
	let bestMs = 0;
	for (const m of messages) {
		if (m._pending) continue;
		const v = m._updatedAt || m.ts;
		const ms = tsMs(v);
		if (ms > bestMs) { bestMs = ms; best = new Date(ms).toISOString(); }
	}
	return best;
}

// 서버가 알려 준 수정본·삭제 목록을 보유 목록에 반영한다 (순수 함수).
//   - 이미 보유한 _id 만 서버 원본으로 교체 — 새 메시지 추가는 ts delta 경로 몫(방 숨김 시스템 메시지 등
//     history API 와 필터가 달라 여기서 넣으면 목록이 어긋난다).
//   - 보유본이 더 최신(_updatedAt 이 더 뒤)이면 교체하지 않는다 — 실시간 푸시가 먼저 왔을 수 있다.
//   - 바뀐 게 없으면 입력 배열을 그대로 반환(참조 동일 → 불필요한 렌더 없음).
export function applySyncChanges(prev: RcMessage[], updated: RcMessage[], deletedIds: string[]): RcMessage[] {
	if (updated.length === 0 && deletedIds.length === 0) return prev;
	const byId = new Map(updated.map((m) => [m._id, m]));
	const gone = new Set(deletedIds);
	let changed = false;
	const out: RcMessage[] = [];
	for (const cur of prev) {
		if (gone.has(cur._id)) { changed = true; continue; }
		const srv = byId.get(cur._id);
		if (srv && !cur._pending && tsMs(srv._updatedAt) > tsMs(cur._updatedAt)) {
			out.push(srv);
			changed = true;
			continue;
		}
		out.push(cur);
	}
	return changed ? out : prev;
}

// 메시지 배열에서 가장 최신(ts 최대) 메시지의 ts 를 구한다. 비어 있으면 null.
//   낙관적 전송 임시 말풍선(_pending)은 서버 ts 가 없으므로 delta 기준에서 제외.
export function newestServerTs(messages: RcMessage[]): string | null {
	return messages.reduce<string | null>(
		(max, m) => (m._pending ? max : !max || m.ts > max ? m.ts : max),
		null,
	);
}

// 🗓 DDP(method.call) 응답의 EJSON 날짜 `{ $date: ms }` → ISO 문자열 (2026-10-19 rcRoomMessagesAfter).
//   REST(*.history)는 ts·_updatedAt 를 ISO 문자열로 주고, 화면·정렬·병합(mergeDeltaMessages 의 문자열 비교)이 전부 그 모양을 가정한다.
//   loadNextMessages 같은 메서드 결과는 EJSON 이라 그대로 섞으면 ts 가 객체가 되어 정렬·날짜 구분선이 깨진다. 깊이 무관 재귀, 입력 불변.
export function reviveEjsonDates<T>(v: T): T {
	if (Array.isArray(v)) return v.map((x) => reviveEjsonDates(x)) as unknown as T;
	if (v && typeof v === 'object') {
		const o = v as Record<string, unknown>;
		const keys = Object.keys(o);
		if (keys.length === 1 && keys[0] === '$date') {
			const d = new Date(typeof o.$date === 'object' && o.$date ? Number((o.$date as { $numberLong?: string }).$numberLong) : o.$date as number | string);
			return (Number.isNaN(d.getTime()) ? v : d.toISOString()) as unknown as T;
		}
		const out: Record<string, unknown> = {};
		for (const k of keys) out[k] = reviveEjsonDates(o[k]);
		return out as T;
	}
	return v;
}
